import classNames from "classnames";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "components/DesignSystem/Carousel/Carousel";
import { SmallCircleGreenTick } from "components/icons/SmallCircleGreenTick";
import { useCallback, useRef, useState } from "react";
import ExpenseRecognitionImage from "static/images/ExpenseRecognitionSlider.png";
import RevenueMetricsImage from "static/images/RevenueMetricsSlider.png";
import AICategorisationImage from "static/images/AICategorization.png";

type Slide = {
  title: string;
  description: string;
  highlights: string[];
  image: string;
};

const SLIDES: Slide[] = [
  {
    title: "Revenue Metrics",
    description:
      "Get a clear view of your revenue, recurring income, and the customers and products behind them.",
    highlights: [
      "Track MRR and ARR effortlessly",
      "Identify top revenue-generating customers",
      "Measure product-wise performance",
    ],
    image: RevenueMetricsImage,
  },
  {
    title: "Expense Tracking",
    description:
      "Monitor your spending with automated categorisation and real-time insights across all accounts.",
    highlights: [
      "Auto-categorise transactions",
      "Track vendor-wise spending",
      "Set budgets and alerts",
    ],
    image: ExpenseRecognitionImage,
  },
  {
    title: "AI Categorization",
    description:
      "Let AI handle the heavy lifting - categorize transactions faster and smarter.",
    highlights: [
      "Auto suggest categories instantly",
      "Bulk categorize repetitive entries",
      "Boost efficiency with automation",
    ],
    image: AICategorisationImage,
  },
];

const SlideContent = ({ slide }: { slide: Slide }) => (
  <div>
    <div className="t-flex t-flex-col t-gap-0.5">
      <h3 className="t-text-subtext t-text-text-100">{slide.title}</h3>
      <p className="t-text-body-sm t-text-text-30">{slide.description}</p>
    </div>
    <div className="t-flex t-flex-col t-gap-2">
      {slide.highlights.map((highlight) => (
        <div
          key={highlight}
          className="t-flex t-items-center t-gap-2 t-text-dark_green"
        >
          <SmallCircleGreenTick />
          <span className="t-text-body-sm t-text-text-30">{highlight}</span>
        </div>
      ))}
    </div>
  </div>
);

export const WhatYouGetCarousel = () => {
  const apiRef = useRef<CarouselApi>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onApiReady = useCallback((api: CarouselApi) => {
    if (!api) return;
    apiRef.current = api;

    setScrollSnaps(api.scrollSnapList());
    setSelectedIndex(api.selectedScrollSnap());

    api.on("select", () => setSelectedIndex(api.selectedScrollSnap()));

    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => api.scrollNext(), 4000);
  }, []);

  return (
    <div className="t-border t-border-solid t-border-surface-grey t-rounded-xl t-overflow-hidden t-shadow-light-30 t-bg-white">
      <div className="t-flex sm:t-flex-col">
        <div className="t-flex t-flex-col t-gap-3 t-w-1/2 t-px-8 t-justify-center sm:t-w-full sm:t-px-4 sm:t-pb-3">
          <span className="t-text-subtext t-text-text-100 t-mt-4">
            What you get with Inkle Books
          </span>
          <Carousel opts={{ loop: true }} setApi={onApiReady}>
            <CarouselContent>
              {SLIDES.map((slide) => (
                <CarouselItem key={slide.title}>
                  <SlideContent slide={slide} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
        <div className="t-w-1/2 t-h-64 t-flex t-items-center t-justify-center t-overflow-hidden sm:t-w-full sm:t-h-48">
          <img
            src={SLIDES[selectedIndex]?.image}
            alt={SLIDES[selectedIndex]?.title}
            className="t-block t-h-full t-w-full t-object-cover"
          />
        </div>
      </div>
      <div className="t-flex t-gap-2 t-justify-center t-pb-4 t-mt-2">
        {scrollSnaps.map((_, index) => (
          <button
            key={index}
            className={classNames(
              "all:unset t-cursor-pointer t-max-w-2 t-max-h-2 t-min-w-2 t-min-h-2 t-rounded-full",
              {
                "t-bg-text-100": index === selectedIndex,
                "t-bg-neutral-20": index !== selectedIndex,
              }
            )}
            onClick={() => apiRef.current?.scrollTo(index)}
          />
        ))}
      </div>
    </div>
  );
};
